import {
  NJButton,
  NJHeading,
  NJIconButton,
  NJInlineMessage,
  NJInputSearch,
  NJPagination,
  NJSpinner,
  NJText,
} from "@engie-group/fluid-design-system-react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ConfirmModal } from "@/components/ConfirmModal";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { SkillCompatibilityTags } from "@/features/skills/SkillCompatibilityTags";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { AdminSkill } from "@/types";
import { useAdminSkills, useDeleteAdminSkill } from "./useAdminSkills";

const PAGE_SIZE = 20;

function StatusCell({ data }: ICellRendererParams<AdminSkill>) {
  if (!data) return null;
  return <StatusBadge status={data.status} />;
}

function HarnessCell({ data }: ICellRendererParams<AdminSkill>) {
  if (!data) return null;
  return (
    <SkillCompatibilityTags items={data.compatibleHarnesses ?? []} max={3} />
  );
}

function ActionsCell({ data }: ICellRendererParams<AdminSkill>) {
  const navigate = useNavigate();
  const deleteSkill = useDeleteAdminSkill();
  const [confirmOpen, setConfirmOpen] = useState(false);
  if (!data) return null;

  return (
    <div className="flex items-center gap-1">
      <NJIconButton
        icon="edit"
        variant="tertiary"
        label={`Edit ${data.name}`}
        onClick={() => navigate(`/admin/skills/${data.slug}/edit`)}
      />
      <NJIconButton
        icon="delete"
        variant="destructive"
        label={`Delete ${data.name}`}
        isDisabled={deleteSkill.isPending}
        onClick={() => setConfirmOpen(true)}
      />
      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title="Delete skill"
        isPending={deleteSkill.isPending}
        onConfirm={() =>
          deleteSkill.mutate(data.slug, {
            onSuccess: () => setConfirmOpen(false),
          })
        }
      >
        Delete skill <strong>{data.name}</strong>? This action cannot be undone.
      </ConfirmModal>
    </div>
  );
}

/** Admin skills table — badge status, harness icons, icon actions, search + pagination.
 * Newest-first ordering surfaces `PENDING` proposals at the top. */
export function AdminSkillsPage() {
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const query = useDebouncedValue(searchInput);

  useEffect(() => setPage(1), [query]);

  const { data, isLoading, isError } = useAdminSkills({
    q: query,
    skip: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  });

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1),
    [data],
  );

  const columnDefs = useMemo<ColDef<AdminSkill>[]>(
    () => [
      {
        headerName: "Slug",
        field: "slug",
        flex: 1,
        cellRenderer: (params: { data?: AdminSkill }) =>
          params.data ? (
            <Link
              to={`/admin/skills/${encodeURIComponent(params.data.slug)}`}
              className="text-nj-brand-solid-default no-underline hover:underline"
            >
              {params.data.slug}
            </Link>
          ) : null,
      },
      { headerName: "Name", field: "name", flex: 1 },
      {
        headerName: "Status",
        cellRenderer: StatusCell,
        flex: 1,
        sortable: false,
      },
      { headerName: "Source", field: "sourceType", flex: 1 },
      { headerName: "Version", field: "currentVersion", flex: 1 },
      {
        headerName: "Compatible harnesses",
        cellRenderer: HarnessCell,
        flex: 2,
        sortable: false,
        autoHeight: true,
      },
      {
        headerName: "Actions",
        cellRenderer: ActionsCell,
        flex: 1,
        sortable: false,
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <NJHeading as="h1" scale="lg">
          Administration — skills
        </NJHeading>
        <Link to="/admin/skills/new">
          <NJButton label="Create skill" icon="add" />
        </Link>
      </div>

      <NJInputSearch
        label="Search skills"
        placeholder="slug, name, version, category, compatibility"
        value={searchInput}
        onChange={(event) => setSearchInput(event.currentTarget.value)}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <NJSpinner aria-label="Loading skills" />
        </div>
      ) : isError ? (
        <NJInlineMessage variant="error">
          Could not load the skills.
        </NJInlineMessage>
      ) : (
        <>
          <DataTable<AdminSkill>
            rowData={data?.items ?? []}
            columnDefs={columnDefs}
          />
          {data && data.items.length === 0 && (
            <NJText
              as="p"
              scale="sm"
              className="text-nj-neutral-secondary-default"
            >
              No skill matches this search.
            </NJText>
          )}
          {totalPages > 1 && (
            <div className="flex justify-end">
              <NJPagination
                count={totalPages}
                currentItem={page}
                ariaLabelPrevious="Previous skills page"
                ariaLabelNext="Next skills page"
                onChange={(item: number) => setPage(item)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
